import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MatchDetector — plain-english widget. Enter a question, see which of
 * Sage's own Library sources most closely matches. Non-oracle by design.
 */
export default function MatchDetector() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sage/match-detect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ q: query, top_k: 3 }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setResult(j);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-sage-border p-6" data-testid="match-detector">
      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-2">
        The Match Detector · which shelf sounds like your question?
      </div>
      <h3 className="text-xl md:text-2xl font-bold tracking-tight uppercase text-sage-text mb-3">
        Ask, and Sage tells you what it's leaning on.
      </h3>
      <p className="text-sm text-sage-muted leading-relaxed mb-4 sage-prose">
        Type a question. The Match Detector runs a plain-language overlap check against every shelf in the Library and returns the strongest hits. Not an oracle — just an honest tell about which sources Sage would likely cite if you asked it.
      </p>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <input
          data-testid="match-detector-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. how do I dispute a debt collector?"
          className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text placeholder-sage-faint focus:outline-none focus:border-sage-accent"
        />
        <button
          type="submit"
          data-testid="match-detector-submit"
          disabled={busy || !q.trim()}
          className="self-start text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Checking…" : "Detect match →"}
        </button>
      </form>

      {err && (
        <div className="mt-4 text-xs text-sage-warn" data-testid="match-detector-error">
          {err}
        </div>
      )}

      {result && (
        <div className="mt-6 space-y-4" data-testid="match-detector-result">
          <div className="border-l-2 border-sage-accent pl-4 text-sm text-sage-muted leading-relaxed sage-prose" data-testid="match-detector-letter">
            {result.letter}
          </div>
          {(result.hits || []).map((h, i) => (
            <div
              key={h.source_id + i}
              className="border border-sage-border p-4"
              data-testid={`match-hit-${i}`}
            >
              <div className="flex justify-between items-baseline flex-wrap gap-2">
                <div className="text-sm font-bold text-sage-text">{h.title}</div>
                <div className="text-xs text-sage-accent tabular-nums">
                  {(h.match_score * 100).toFixed(1)}% overlap
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-1">
                {h.shelf_name} · {h.source_id}
              </div>
              <div className="text-xs text-sage-muted mt-2 leading-relaxed">
                {h.excerpt}
              </div>
              <a
                href={h.url}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] uppercase tracking-[0.1em] text-sage-accent hover:underline mt-2 inline-block"
              >
                open source ↗
              </a>
            </div>
          ))}
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint pt-2 border-t border-sage-border">
            Algorithm: {result.algorithm || "lexical-overlap-v1"} · honest on the wire.
          </div>
        </div>
      )}
    </div>
  );
}
